import type { EqualizationComparison, EqualizationCurrencyGroup, PurchaseRequestStatus } from '../api';

export const PURCHASE_STATUS_LABELS: Record<PurchaseRequestStatus, string> = {
  draft: 'Rascunho',
  pending_approval: 'Aguardando aprovação',
  returned: 'Devolvida',
  approved: 'Aprovada',
  rejected: 'Rejeitada',
  in_quotation: 'Em cotação',
  pending_selection_approval: 'Seleção em aprovação',
  finance_pending: 'Aguardando Financeiro',
  payment_in_progress: 'Pagamento em andamento',
  paid: 'Paga',
  closed: 'Encerrada',
  cancelled: 'Cancelada',
};

export const PURCHASE_STATUS_STYLES: Record<PurchaseRequestStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending_approval: 'bg-amber-100 text-amber-800',
  returned: 'bg-orange-100 text-orange-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-700',
  in_quotation: 'bg-blue-100 text-blue-800',
  pending_selection_approval: 'bg-violet-100 text-violet-800',
  finance_pending: 'bg-cyan-100 text-cyan-800',
  payment_in_progress: 'bg-sky-100 text-sky-800',
  paid: 'bg-green-100 text-green-800',
  closed: 'bg-slate-200 text-slate-800',
  cancelled: 'bg-gray-200 text-gray-600',
};

export function formatMoney(value: number | string | null | undefined, currency = 'BRL'): string {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(amount);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('pt-BR');
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('pt-BR');
}

export function formatBytes(value: number | string | null | undefined): string {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} KB`;
  return `${(bytes / 1024 ** 2).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
}

function normalizeAction(value: string): string {
  return value.trim().toLowerCase().replace(/[.:\s-]+/g, '_');
}

/**
 * Ações são capacidades emitidas pelo backend. O frontend aceita apenas
 * aliases nominais do mesmo comando; jamais deduz autorização pelo status.
 */
export function hasAvailableAction(actions: string[] | undefined, ...aliases: string[]): boolean {
  if (!actions?.length) return false;
  const granted = new Set(actions.map(normalizeAction));
  return aliases.some((alias) => granted.has(normalizeAction(alias)));
}

export function referenceLabel(value: {
  id: string;
  name?: string;
  code?: string;
  corporateName?: string;
  tradeName?: string | null;
  razaoSocial?: string;
  nomeFantasia?: string | null;
} | null | undefined): string {
  if (!value) return '—';
  const name = value.tradeName ?? value.nomeFantasia ?? value.name ?? value.corporateName ?? value.razaoSocial;
  if (!name) return value.code ?? 'Registro cadastrado';
  return value.code ? `${value.code} — ${name}` : name;
}

export function comparisonCurrencyGroups(
  comparison: EqualizationComparison | undefined,
): EqualizationCurrencyGroup[] {
  if (!comparison) return [];
  if (comparison.currencyGroups?.length) return comparison.currencyGroups;
  return [{
    currency: comparison.currency ?? 'BRL',
    matrix: comparison.matrix ?? [],
    global: comparison.global ?? [],
    lowestQuotationId: comparison.lowestQuotationId,
  }];
}
